import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Container,
  Modal,
  TextField,
  AppBar,
  Typography,
  Toolbar,
  Button,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import { useNavigate } from "react-router-dom";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { Info as InfoIcon } from '@mui/icons-material';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const AddConcession = ({ onSave, onCancel }) => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  let navigate = useNavigate();
  const [concessionName, setConcessionName] = useState('');
  const [remark, setRemark] = useState('');
  const [loadingBarProgress, setLoadingBarProgress] = useState('');

  // Info modal state
  const [showInfoModal, setShowInfoModal] = useState(false);

  const handleSave = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      setLoadingBarProgress(30);
      const response = await fetch(`${apiUrl}/Concession`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: sessionStorage.getItem('token'),
        },
        body: JSON.stringify({
          concessionName: concessionName,
          remark: remark,
        }),
      });

      if (response.ok) {
        const responseData = await response.json();
        setLoadingBarProgress(100);
        setShowSuccessModal(true);
        setConcessionName('');
        setRemark('');

      } else {
        setLoadingBarProgress(0);
        // alert('Unable to add concession');
        setSnackbarMessage('Unable to add concession');
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error('API request error:', error);
      // alert('An error occurred. Please try again later.');
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  const handleCancel = () => {
    // onCancel();
    setConcessionName('');
    setRemark('');
  };

  // Info sections data
  const infoSections = [
    {
      title: "📋 Page Overview",
      content:
        "The Add Concession page allows authorized staff to create a new fee concession record in the system. Fill in the concession name and an optional remark, then click SAVE to submit.",
    },
    {
      title: "📝 Form Fields",
      items: [
        "Concession Name – The name of the new concession (e.g., Sibling Discount, Merit-Based, Staff Concession). This is the primary identifier for the concession.",
        "Remark – An optional description or note providing additional context about the concession.",
      ],
    },
    {
      title: "⚙️ Actions",
      items: [
        "SAVE – Submits the new concession to the system. Shows a success dialog on completion or an error message if the operation fails.",
        "RESET – Clears both the Concession Name and Remark fields so you can start over.",
      ],
    },
    {
      title: "✅ After Saving",
      content:
        "A confirmation dialog appears confirming the concession was added successfully. Click OK to close the dialog and return to the previous page.",
    },
  ];

  return (
    <Container
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '50vh',
      }}
    >
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h4" component="div" style={{ fontWeight: 'bold' }}>
            Add Concession
          </Typography>

          {/* Info Icon Button */}
          <Tooltip title="View page information & guide" arrow>
            <IconButton
              onClick={() => setShowInfoModal(true)}
              size="small"
              sx={{
                color: "rgba(255,255,255,0.75)",
                backgroundColor: "rgba(255,255,255,0.1)",
                border: "1.5px solid rgba(255,255,255,0.25)",
                borderRadius: "50%",
                width: 30,
                height: 30,
                ml: 0.5,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(100,181,246,0.25)",
                  borderColor: "#64B5F6",
                  color: "#64B5F6",
                  transform: "scale(1.1)",
                },
              }}
            >
              <InfoIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Paper elevation={3} style={{ padding: 16, width: '100%', margin: 'auto', marginTop: 6 }}>
        <form>
          <Box marginBottom="20px">
            <TextField
              label="Concession Name"
              variant="outlined"
              value={concessionName}
              onChange={(e) => setConcessionName(e.target.value)}
              placeholder="Enter Concession Name"
              sx={{ width: '100%' }}
            />
          </Box>

          <Box marginBottom="20px">
            <TextField
              label="Remark"
              variant="outlined"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="Enter Remark"
              sx={{ width: '100%' }}
            />
          </Box>

          <Box>
          <Tooltip title={!concessionName.trim() ? "Enter concession name" : ""} arrow>
  <span>
    <Button
      variant="contained"
      color="primary"
      onClick={handleSave}
      disabled={!concessionName.trim()}
      style={{ marginRight: '10px' }}
    >
      SAVE
    </Button>
  </span>
</Tooltip>
            <Button variant="contained" color="error" onClick={handleCancel}>
              RESET
            </Button>
          </Box>
        </form>
      </Paper>

      {/* ===== Info Modal ===== */}
      <Modal
        open={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        aria-labelledby="info-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: 520 },
            maxHeight: '85vh',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: '0 8px 40px rgba(11,31,61,0.22)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              backgroundColor: '#0B1F3D',
              padding: '14px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon sx={{ color: '#64B5F6', fontSize: 22 }} />
              <Typography id="info-modal-title" variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                Add Concession — Page Guide
              </Typography>
            </Box>
            {/* Close Button */}
           
          </Box>

          {/* Modal Body — scrollable */}
          <Box sx={{ overflowY: 'auto', padding: '20px 24px', flex: 1 }}>
            {infoSections.map((section, idx) => (
              <Box key={idx} sx={{ mb: 2.5 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: '#0B1F3D',
                    mb: 0.8,
                    fontSize: '0.97rem',
                  }}
                >
                  {section.title}
                </Typography>

                {section.content && (
                  <Typography
                    variant="body2"
                    sx={{ color: '#444', lineHeight: 1.7, fontSize: '0.875rem' }}
                  >
                    {section.content}
                  </Typography>
                )}

                {section.items && (
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {section.items.map((item, i) => (
                      <Box
                        component="li"
                        key={i}
                        sx={{
                          fontSize: '0.875rem',
                          color: '#444',
                          lineHeight: 1.75,
                          mb: 0.3,
                        }}
                      >
                        {item}
                      </Box>
                    ))}
                  </Box>
                )}

                {idx < infoSections.length - 1 && (
                  <Divider sx={{ mt: 2, borderColor: 'rgba(11,31,61,0.08)' }} />
                )}
              </Box>
            ))}
          </Box>

          {/* Modal Footer */}
          <Box
            sx={{
              padding: '12px 24px',
              borderTop: '1px solid rgba(11,31,61,0.1)',
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: '#f9fafb',
            }}
          >
            <Button
              variant="contained"
              onClick={() => setShowInfoModal(false)}
              sx={{
                backgroundColor: '#0B1F3D',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { backgroundColor: '#1a3560' },
              }}
            >
              Got it
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Concession Added ✅
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              The Concession has been added successfully.
            </Typography>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1); // go back after closing modal
                }}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

    </Container>
  );
};

export default AddConcession;